import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Upload, FileText, X } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { validateEmail, validatePhone, validateName, validateCountry, sanitizeText, checkRateLimit } from '@/lib/security';


interface JobApplicationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  positionType: string;
}

const JobApplicationModal = ({ open, onOpenChange, positionType }: JobApplicationModalProps) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    country: '',
    email: '',
    phone: '',
  });
  const [cvFile, setCvFile] = useState<File | null>(null);
  const [coverLetterFile, setCoverLetterFile] = useState<File | null>(null);
  const [privacyPolicyAccepted, setPrivacyPolicyAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'cv' | 'coverLetter') => {
    const file = e.target.files?.[0];
    if (file) {
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        toast({
          title: t('jobApplication.fileTooLarge'),
          description: t('jobApplication.fileTooLargeDescription'),
          variant: "destructive",
        });
        return;
      }
      
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        toast({
          title: t('jobApplication.invalidFileType'),
          description: t('jobApplication.invalidFileTypeDescription'),
          variant: "destructive",
        });
        return;
      }

      if (type === 'cv') {
        setCvFile(file);
      } else {
        setCoverLetterFile(file);
      }
    }
  };

  const removeFile = (type: 'cv' | 'coverLetter') => {
    if (type === 'cv') {
      setCvFile(null);
    } else {
      setCoverLetterFile(null);
    }
  };

  const uploadFile = async (file: File, fileName: string): Promise<string | null> => {
    try {
      const { data, error } = await supabase.storage
        .from('job-applications')
        .upload(fileName, file);

      if (error) throw error;
      return data.path;
    } catch (error) {
      console.error('Error uploading file:', error);
      return null;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Rate limiting check (3 applications per hour)
    if (!checkRateLimit('job_application', 3, 60 * 60 * 1000)) {
      toast({
        title: "Too many applications",
        description: "You can only submit 3 applications per hour. Please try again later.",
        variant: "destructive",
      });
      return;
    }

    // Basic validation
    if (!formData.name || !formData.country || !formData.email || !formData.phone) {
      toast({
        title: t('jobApplication.missingFields'),
        description: t('jobApplication.missingFieldsDescription'),
        variant: "destructive",
      });
      return;
    }

    // Enhanced validation
    if (!validateName(formData.name)) {
      toast({
        title: "Invalid name",
        description: "Name must contain only letters and be 2-50 characters long.",
        variant: "destructive",
      });
      return;
    }

    if (!validateEmail(formData.email)) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    if (!validatePhone(formData.phone)) {
      toast({
        title: "Invalid phone number",
        description: "Please enter a valid phone number (10-20 characters).",
        variant: "destructive",
      });
      return;
    }

    if (!validateCountry(formData.country)) {
      toast({
        title: "Invalid country",
        description: "Country must contain only letters and be 2-50 characters long.",
        variant: "destructive",
      });
      return;
    }

    if (!cvFile) {
      toast({
        title: t('jobApplication.cvRequired'),
        description: t('jobApplication.cvRequiredDescription'),
        variant: "destructive",
      });
      return;
    }

    // Validate privacy policy acceptance
    if (!privacyPolicyAccepted) {
      toast({
        title: "Privacy Policy Required",
        description: "Please accept the privacy policy to continue.",
        variant: "destructive",
      });
      return;
    }


    setIsSubmitting(true);

    try {
      const timestamp = Date.now();
      const sanitizedName = formData.name.replace(/[^a-zA-Z0-9]/g, '_');
      
      // Upload CV
      const cvFileName = `${timestamp}_${sanitizedName}_cv.${cvFile.name.split('.').pop()}`;
      const cvPath = await uploadFile(cvFile, cvFileName);
      
      if (!cvPath) {
        throw new Error('Failed to upload CV');
      }

      // Upload cover letter if provided
      let coverLetterPath = null;
      if (coverLetterFile) {
        const coverLetterFileName = `${timestamp}_${sanitizedName}_cover_letter.${coverLetterFile.name.split('.').pop()}`;
        coverLetterPath = await uploadFile(coverLetterFile, coverLetterFileName);
      }

      // Save application to database with sanitized data
      const { error: dbError } = await supabase
        .from('job_applications')
        .insert({
          position_type: sanitizeText(positionType),
          name: sanitizeText(formData.name),
          country: sanitizeText(formData.country),
          email: sanitizeText(formData.email),
          phone: sanitizeText(formData.phone),
          cv_url: cvPath,
          cover_letter_url: coverLetterPath,
        });

      if (dbError) {
        console.error('Database error:', dbError);
        // Continue with email even if database fails
      }

      // Send email notification with sanitized data
      const { error: emailError } = await supabase.functions.invoke('send-job-application', {
        body: {
          positionType: sanitizeText(positionType),
          name: sanitizeText(formData.name),
          country: sanitizeText(formData.country),
          email: sanitizeText(formData.email),
          phone: sanitizeText(formData.phone),
          cvUrl: cvPath ? `${supabase.storage.from('job-applications').getPublicUrl(cvPath).data.publicUrl}` : undefined,
          coverLetterUrl: coverLetterPath ? `${supabase.storage.from('job-applications').getPublicUrl(coverLetterPath).data.publicUrl}` : undefined
        },
      });

      if (emailError) {
        console.error('Email error:', emailError);
        toast({
          title: t('jobApplication.partialSuccess'),
          description: t('jobApplication.partialSuccessDescription'),
          variant: "destructive",
        });
      } else {
        toast({
          title: t('jobApplication.applicationSubmitted'),
          description: t('applicationSubmittedDescription'),
        });
      }

      // Reset form
      setFormData({ name: '', country: '', email: '', phone: '' });
      setCvFile(null);
      setCoverLetterFile(null);
      setPrivacyPolicyAccepted(false);
      onOpenChange(false);

    } catch (error) {
      console.error('Error submitting application:', error);
      toast({
        title: t('jobApplication.submissionFailed'),
        description: t('jobApplication.submissionFailedDescription'),
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('jobApplication.title').replace('{position}', positionType)}</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">{t('jobApplication.fullName')}</Label>
            <Input
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              placeholder={t('jobApplication.fullNamePlaceholder')}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="country">{t('jobApplication.country')}</Label>
            <Input
              id="country"
              name="country"
              value={formData.country}
              onChange={handleInputChange}
              placeholder={t('jobApplication.countryPlaceholder')}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">{t('jobApplication.email')}</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              placeholder={t('jobApplication.emailPlaceholder')}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="phone">{t('jobApplication.phone')}</Label>
            <Input
              id="phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleInputChange}
              placeholder={t('jobApplication.phonePlaceholder')}
              required
            />
          </div>

          <div className="space-y-2">
            <Label>{t('jobApplication.cvLabel')}</Label>
            {!cvFile ? (
              <div className="relative">
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => handleFileChange(e, 'cv')}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  id="cv-upload"
                />
                <div className="border-2 border-dashed border-border rounded-lg p-4 text-center hover:border-primary/50 transition-colors cursor-pointer">
                  <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground mb-2">{t('jobApplication.cvUploadText')}</p>
                  <Button type="button" variant="outline" size="sm" className="pointer-events-none">
                    {t('jobApplication.chooseFile')}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium">{cvFile.name}</span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeFile('cv')}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label>{t('jobApplication.coverLetterLabel')}</Label>
            {!coverLetterFile ? (
              <div className="relative">
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => handleFileChange(e, 'coverLetter')}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                  id="cover-letter-upload"
                />
                <div className="border-2 border-dashed border-border rounded-lg p-4 text-center hover:border-primary/50 transition-colors cursor-pointer">
                  <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground mb-2">{t('jobApplication.coverLetterUploadText')}</p>
                  <Button type="button" variant="outline" size="sm" className="pointer-events-none">
                    {t('jobApplication.chooseFile')}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-primary" />
                  <span className="text-sm font-medium">{coverLetterFile.name}</span>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => removeFile('coverLetter')}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>

          <div className="flex items-center space-x-2 py-2">
            <Checkbox
              id="privacy-policy-job"
              checked={privacyPolicyAccepted}
              onCheckedChange={(checked) => setPrivacyPolicyAccepted(checked as boolean)}
            />
            <Label htmlFor="privacy-policy-job" className="text-sm">
              I accept the{' '}
              <a
                href="/commodIT_PrivacyPolicy_hu.pdf"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                privacy policy
              </a>
            </Label>
          </div>


          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
              className="flex-1"
            >
              {t('jobApplication.cancel')}
            </Button>
            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="flex-1"
            >
              {isSubmitting ? t('jobApplication.submitting') : t('jobApplication.submit')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default JobApplicationModal;